"use client";

// Solicitar demo v3 — card centrado sobre crema. Form postea al endpoint
// propio /api/demo-request (antes iba a Formspree, que rompía por CSP
// en producción + tenía cap de 50/mes en free tier).
//
// Lógica:
//   - POST a /api/demo-request con los campos del form
//   - Endpoint manda mail a hola@talenv.com vía Resend (ya integrado)
//   - Rate limit por IP en server (5 cada 15 min)
//   - Country code auto-derivado del país seleccionado
//   - Sent state con confirmación + CTA "Volver al inicio"

import Link from "next/link";
import { useState } from "react";
import { PublicShell } from "@/components/brand/PublicShell";
import { BrandMark } from "@/components/brand/BrandMark";

const INDUSTRIES = [
  "Tecnología", "Salud y Medicina", "Educación", "Retail y Comercio",
  "Manufactura e Industria", "Servicios Profesionales", "Gastronomía y Hotelería",
  "Construcción e Inmobiliaria", "Finanzas y Seguros", "Logística y Transporte",
  "Agro y Agroindustria", "Medios y Entretenimiento", "Recursos Humanos / Consultoría",
  "Otra",
];
const COUNTRIES = ["Argentina", "Chile", "Uruguay", "Paraguay", "Bolivia", "Colombia", "México", "España", "Otro"];
const COUNTRY_CODES: Record<string, string> = {
  Argentina: "+54", Chile: "+56", Uruguay: "+598", Paraguay: "+595",
  Bolivia: "+591", Colombia: "+57", México: "+52", España: "+34", Otro: "+",
};
const ROLES = [
  "CEO / Dueño", "Director/a de RRHH", "Gerente de RRHH", "Responsable de RRHH",
  "Recruiter / Headhunter", "Gerente General", "Gerente de Operaciones", "Otro",
];
const EMPLOYEE_RANGES = ["1 a 10", "11 a 50", "51 a 200", "201 a 500", "501 a 1.000", "Más de 1.000"];
const SEARCH_RANGES = ["1 a 5", "6 a 20", "21 a 50", "51 a 100", "Más de 100"];

export default function DemoPage({ params }: { params: { locale: string } }) {
  const [form, setForm] = useState({
    fullName: "", email: "", company: "", industry: "", country: "",
    phone: "", role: "", employees: "", searches: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneCode = COUNTRY_CODES[form.country] ?? "+54";

  function set<K extends keyof typeof form>(key: K) {
    return (value: string) => setForm((p) => ({ ...p, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const res = await fetch("/api/demo-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: form.fullName, email: form.email, empresa: form.company,
          industria: form.industry, pais: form.country,
          telefono: `${phoneCode} ${form.phone}`,
          cargo: form.role, colaboradores: form.employees,
          busquedas_anuales: form.searches, mensaje: form.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error((data as any)?.error ?? "Error al enviar el formulario");
      }
      setSent(true);
    } catch (err: any) {
      setError(err?.message ?? "Ocurrió un error. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <PublicShell locale={params.locale}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: "#6C5CFF", color: "white",
            display: "grid", placeItems: "center",
            margin: "0 auto 18px",
            boxShadow: "0 8px 22px -8px rgba(108,92,255,0.6), inset 0 1px 0 rgba(255,255,255,0.3)",
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <h1 className="card-title">¡Listo! Recibimos tu solicitud</h1>
          <p className="card-sub" style={{ marginTop: 8 }}>
            En breve te vamos a contactar por email con la confirmación y los detalles de tu demo personalizada.
          </p>
          <div className="card-foot" style={{ marginTop: 24 }}>
            <Link href={`/${params.locale}`}>← Volver al inicio</Link>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell locale={params.locale} maxWidth={560}>
      <div className="card-header">
        <div className="card-icon">
          <BrandMark size={44} rounded={11} />
          talenv
        </div>
        <h1 className="card-title">Agendá tu demo</h1>
        <p className="card-sub">
          Contanos sobre tu equipo y nos ponemos en contacto por email para coordinar 20 min.
        </p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <Grid2>
          <Field label="Nombre y apellido" required>
            <input type="text" className="input" placeholder="Juan Pérez"
              value={form.fullName} onChange={(e) => set("fullName")(e.target.value)} required />
          </Field>
          <Field label="Email corporativo" required>
            <input type="email" className="input" placeholder="vos@empresa.com"
              value={form.email} onChange={(e) => set("email")(e.target.value)} required />
          </Field>
        </Grid2>

        <Grid2>
          <Field label="Nombre de la compañía" required>
            <input type="text" className="input" placeholder="Tu empresa"
              value={form.company} onChange={(e) => set("company")(e.target.value)} required />
          </Field>
          <Field label="Industria" required>
            <select className="select" value={form.industry} onChange={(e) => set("industry")(e.target.value)} required>
              <option value="" disabled>Seleccioná una opción</option>
              {INDUSTRIES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>
        </Grid2>

        <Grid2>
          <Field label="País" required>
            <select className="select" value={form.country} onChange={(e) => set("country")(e.target.value)} required>
              <option value="" disabled>Seleccioná un país</option>
              {COUNTRIES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>
          <Field label="Teléfono de contacto" required>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{
                display: "grid", placeItems: "center", padding: "0 12px",
                borderRadius: 10, border: "1px solid rgba(21,16,61,0.14)",
                background: "white", color: "#6B6982",
                fontFamily: "ui-monospace, monospace", fontSize: 13.5, fontWeight: 500,
                minWidth: 64, flexShrink: 0,
              }}>
                {phoneCode}
              </div>
              <input type="tel" className="input" placeholder="11 1234 5678"
                value={form.phone} onChange={(e) => set("phone")(e.target.value)} required />
            </div>
          </Field>
        </Grid2>

        <Grid2>
          <Field label="Cargo" required>
            <select className="select" value={form.role} onChange={(e) => set("role")(e.target.value)} required>
              <option value="" disabled>Seleccioná tu rol</option>
              {ROLES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>
          <Field label="Cantidad de colaboradores" required>
            <select className="select" value={form.employees} onChange={(e) => set("employees")(e.target.value)} required>
              <option value="" disabled>Seleccioná un rango</option>
              {EMPLOYEE_RANGES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </Field>
        </Grid2>

        <Field label="Búsquedas anuales" required>
          <select className="select" value={form.searches} onChange={(e) => set("searches")(e.target.value)} required>
            <option value="" disabled>Seleccioná un rango</option>
            {SEARCH_RANGES.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </Field>

        <Field label="¿Cómo te puede ayudar Talenv?" required>
          <textarea className="textarea" rows={4}
            placeholder="Contanos sobre tu proceso actual, los desafíos que enfrentás y qué esperás de la demo…"
            value={form.message} onChange={(e) => set("message")(e.target.value)} required />
        </Field>

        {error && <div className="error-box">{error}</div>}

        <button type="submit" className="btn-submit" disabled={loading}>
          {loading ? "Enviando…" : "Solicitar mi demo"}
        </button>

        <p style={{ marginTop: 12, fontSize: 11.5, color: "#A19FB6", textAlign: "center", lineHeight: 1.55 }}>
          Al enviar, aceptás nuestra{" "}
          <Link href={`/${params.locale}/privacy`} style={{ color: "#6C5CFF", textDecoration: "underline" }}>
            política de privacidad
          </Link>.
        </p>
      </form>

      <div className="card-foot">
        <Link href={`/${params.locale}`}>← Volver al inicio</Link>
      </div>
    </PublicShell>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="field">
      <label className="field-label">
        {label}{required && <span style={{ color: "#6C5CFF", marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="demo-grid">
      <style jsx>{`
        @media (max-width: 540px) {
          .demo-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {children}
    </div>
  );
}
